import { makeVar, useReactiveVar } from '@apollo/client';
import { localStorageService } from '@/services/local-storage-service';
import { stopReadAloud } from '@/utils/audio-utils/read-aloud.ts';

export enum SoundTrack {
  DigitalButton = 'digital-button.mp3',
  FingerSnap = 'finger-snap.mp3',
  GlockenspielDing = 'glockenspiel-ding.mp3',
  NotificationSweep = 'notification-sweep.mp3',
  ReadAloudFemale = 'read-aloud-female',
  ReadAloudMale = 'read-aloud-male',
}

export const SOUND_TRACK_LABELS: Record<SoundTrack, string> = {
  [SoundTrack.DigitalButton]: 'Digital Button',
  [SoundTrack.FingerSnap]: 'Finger Snap',
  [SoundTrack.GlockenspielDing]: 'Glockenspiel Ding',
  [SoundTrack.NotificationSweep]: 'Notification Sweep',
  [SoundTrack.ReadAloudFemale]: 'Read Aloud (Female)',
  [SoundTrack.ReadAloudMale]: 'Read Aloud (Male)',
};

export const isReadAloudTrack = (
  track: SoundTrack,
): track is SoundTrack.ReadAloudFemale | SoundTrack.ReadAloudMale =>
  track === SoundTrack.ReadAloudFemale || track === SoundTrack.ReadAloudMale;

export const DEFAULT_SOUND_TRACK = SoundTrack.DigitalButton;
export const DEFAULT_SOUND_VOLUME = 1;

export interface HighlightSoundSettings {
  enabled: boolean;
  track: SoundTrack;
  volume: number;
}

export interface CollectionSoundSettings {
  track: SoundTrack;
  volume: number;
  highlightSound: HighlightSoundSettings;
  matchSound: HighlightSoundSettings;
  /** Narrows the sound to the categories below instead of every post. */
  muteOtherPosts: boolean;
  /** Which categories still play while `muteOtherPosts` is on. */
  playHighlights: boolean;
  playMatches: boolean;
}

export const DEFAULT_HIGHLIGHT_SOUND_TRACK = SoundTrack.FingerSnap;
export const DEFAULT_MATCH_SOUND_TRACK = SoundTrack.GlockenspielDing;

export type CollectionSoundSettingsMap = Record<
  string,
  CollectionSoundSettings
>;

const SOUND_SETTINGS_KEY = 'collection-sound-settings';

export const collectionSoundSettingsVar = makeVar<CollectionSoundSettingsMap>(
  {},
);

const initializeSoundSettings = () => {
  try {
    const stored = localStorageService.getItem(SOUND_SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as CollectionSoundSettingsMap;
      collectionSoundSettingsVar(parsed);
    }
  } catch {
    // Invalid JSON, ignore
  }
};

initializeSoundSettings();

export const getCollectionSoundSettings = (
  collectionId: string,
): CollectionSoundSettings => {
  const currentMap = collectionSoundSettingsVar();

  if (!(collectionId in currentMap)) {
    return {
      track: DEFAULT_SOUND_TRACK,
      volume: DEFAULT_SOUND_VOLUME,
      highlightSound: {
        enabled: false,
        track: DEFAULT_HIGHLIGHT_SOUND_TRACK,
        volume: DEFAULT_SOUND_VOLUME,
      },
      matchSound: {
        enabled: false,
        track: DEFAULT_MATCH_SOUND_TRACK,
        volume: DEFAULT_SOUND_VOLUME,
      },
      muteOtherPosts: false,
      playHighlights: true,
      playMatches: true,
    };
  }

  const stored = currentMap[collectionId];
  const legacyOnlyHighlights = (stored as { onlyHighlights?: boolean })
    .onlyHighlights;

  // Settings persisted before a category existed are missing its branch
  return {
    track: stored.track || DEFAULT_SOUND_TRACK,
    volume: stored.volume ?? DEFAULT_SOUND_VOLUME,
    highlightSound: {
      enabled: stored.highlightSound?.enabled ?? false,
      track: stored.highlightSound?.track || DEFAULT_HIGHLIGHT_SOUND_TRACK,
      volume: stored.highlightSound?.volume ?? DEFAULT_SOUND_VOLUME,
    },
    matchSound: {
      enabled: stored.matchSound?.enabled ?? false,
      track: stored.matchSound?.track || DEFAULT_MATCH_SOUND_TRACK,
      volume: stored.matchSound?.volume ?? DEFAULT_SOUND_VOLUME,
    },
    muteOtherPosts: stored.muteOtherPosts ?? legacyOnlyHighlights ?? false,
    playHighlights: stored.playHighlights ?? true,
    playMatches: stored.playMatches ?? !legacyOnlyHighlights,
  };
};

export const setCollectionSoundSettings = (
  collectionId: string,
  settings: CollectionSoundSettings,
) => {
  const currentMap = collectionSoundSettingsVar();
  const updatedMap = { ...currentMap, [collectionId]: settings };

  collectionSoundSettingsVar(updatedMap);
  localStorageService.setItem(SOUND_SETTINGS_KEY, JSON.stringify(updatedMap));
};

export const setCollectionSoundTrack = (
  collectionId: string,
  track: SoundTrack,
) => {
  const current = getCollectionSoundSettings(collectionId);
  if (isReadAloudTrack(current.track) && !isReadAloudTrack(track)) {
    stopReadAloud(collectionId);
  }
  setCollectionSoundSettings(collectionId, { ...current, track });
};

export const setCollectionSoundVolume = (
  collectionId: string,
  volume: number,
) => {
  const current = getCollectionSoundSettings(collectionId);
  setCollectionSoundSettings(collectionId, { ...current, volume });
};

type CategoryKey = 'highlightSound' | 'matchSound';

const setCategorySoundEnabled = (
  collectionId: string,
  key: CategoryKey,
  enabled: boolean,
) => {
  const current = getCollectionSoundSettings(collectionId);
  setCollectionSoundSettings(collectionId, {
    ...current,
    [key]: { ...current[key], enabled },
  });
};

const setCategorySoundTrack = (
  collectionId: string,
  key: CategoryKey,
  track: SoundTrack,
) => {
  const current = getCollectionSoundSettings(collectionId);
  if (isReadAloudTrack(current[key].track) && !isReadAloudTrack(track)) {
    stopReadAloud(collectionId);
  }
  setCollectionSoundSettings(collectionId, {
    ...current,
    [key]: { ...current[key], track },
  });
};

const setCategorySoundVolume = (
  collectionId: string,
  key: CategoryKey,
  volume: number,
) => {
  const current = getCollectionSoundSettings(collectionId);
  setCollectionSoundSettings(collectionId, {
    ...current,
    [key]: { ...current[key], volume },
  });
};

export const setHighlightSoundEnabled = (
  collectionId: string,
  enabled: boolean,
) => setCategorySoundEnabled(collectionId, 'highlightSound', enabled);

export const setHighlightSoundTrack = (
  collectionId: string,
  track: SoundTrack,
) => setCategorySoundTrack(collectionId, 'highlightSound', track);

export const setHighlightSoundVolume = (collectionId: string, volume: number) =>
  setCategorySoundVolume(collectionId, 'highlightSound', volume);

export const setMatchSoundEnabled = (collectionId: string, enabled: boolean) =>
  setCategorySoundEnabled(collectionId, 'matchSound', enabled);

export const setMatchSoundTrack = (collectionId: string, track: SoundTrack) =>
  setCategorySoundTrack(collectionId, 'matchSound', track);

export const setMatchSoundVolume = (collectionId: string, volume: number) =>
  setCategorySoundVolume(collectionId, 'matchSound', volume);

export const setMuteOtherPosts = (
  collectionId: string,
  muteOtherPosts: boolean,
) => {
  const current = getCollectionSoundSettings(collectionId);
  setCollectionSoundSettings(collectionId, { ...current, muteOtherPosts });
};

export const setPlayHighlights = (
  collectionId: string,
  playHighlights: boolean,
) => {
  const current = getCollectionSoundSettings(collectionId);
  setCollectionSoundSettings(collectionId, { ...current, playHighlights });
};

export const setPlayMatches = (collectionId: string, playMatches: boolean) => {
  const current = getCollectionSoundSettings(collectionId);
  setCollectionSoundSettings(collectionId, { ...current, playMatches });
};

/**
 * Scope decides whether a post plays, then the category picks the track. A
 * feed only narrows to categories it can see, or a scope set on another
 * surface would silence it with no switch here to undo that.
 */
export const resolvePostSound = (
  settings: CollectionSoundSettings,
  post: {
    hasHighlight: boolean;
    hasMatch: boolean;
    highlightsAvailable: boolean;
    matchesAvailable: boolean;
  },
): { track: SoundTrack; volume: number; priority: boolean } | null => {
  const narrowed =
    settings.muteOtherPosts &&
    ((post.highlightsAvailable && settings.playHighlights) ||
      (post.matchesAvailable && settings.playMatches));

  const inScope =
    !narrowed ||
    (post.hasHighlight && settings.playHighlights) ||
    (post.hasMatch && settings.playMatches);
  if (!inScope) return null;

  // A post can be both; the highlight is the user's own keyword, so it wins
  // over a market the enrichers matched.
  if (post.hasHighlight && settings.highlightSound.enabled) {
    return { ...settings.highlightSound, priority: true };
  }
  if (post.hasMatch && settings.matchSound.enabled) {
    return { ...settings.matchSound, priority: true };
  }
  return { track: settings.track, volume: settings.volume, priority: false };
};

export const useCollectionSoundSettings = (
  collectionId: string,
): CollectionSoundSettings => {
  useReactiveVar(collectionSoundSettingsVar);
  return getCollectionSoundSettings(collectionId);
};
